/**
 * Wizard Step 3 - Validation
 *
 * Review variances and validate inventory.
 * @see US-12.7 - Step 3: Validation
 * @see US-12.5 - Preview avec recapitulatif
 */

import { AlertTriangle, Check, TrendingUp, TrendingDown, Package } from 'lucide-react';
import { useCurrency } from '@/hooks';
import { formatCentimes, formatQuantity } from '@/lib/format';
import { Checkbox } from '@/components/ui';
import type { InventoryWithLines, InventoryPreviewResponse } from '@bstock/shared';

interface WizardStep3Props {
  inventory: InventoryWithLines;
  preview: InventoryPreviewResponse;
  isConfirmed: boolean;
  onConfirmationChange: (confirmed: boolean) => void;
}

export function WizardStep3({ inventory: _inventory, preview, isConfirmed, onConfirmationChange }: WizardStep3Props) {
  const currency = useCurrency();
  const { summary, linesWithVariance, movementsToGenerate } = preview;

  const hasVariances = summary.linesWithVariance > 0;
  const uncountedLines = summary.totalLines - summary.countedLines;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-neutral-50 rounded-lg p-4">
          <div className="text-sm text-neutral-500 mb-1">Lignes comptees</div>
          <div className="text-2xl font-semibold text-dark-500">
            {summary.countedLines} / {summary.totalLines}
          </div>
          {uncountedLines > 0 && (
            <div className="text-xs text-warning-600 mt-1">
              {uncountedLines} ligne(s) non comptee(s)
            </div>
          )}
        </div>

        <div className="bg-neutral-50 rounded-lg p-4">
          <div className="text-sm text-neutral-500 mb-1">Ecarts detectes</div>
          <div className={`text-2xl font-semibold ${hasVariances ? 'text-warning-600' : 'text-success-600'}`}>
            {summary.linesWithVariance}
          </div>
          <div className="text-xs text-neutral-500 mt-1">
            {movementsToGenerate} mouvement(s) a generer
          </div>
        </div>

        <div className="bg-neutral-50 rounded-lg p-4">
          <div className="text-sm text-neutral-500 mb-1">Valeur nette des ecarts</div>
          <div className={`text-2xl font-semibold ${
            summary.netVarianceValue < 0 ? 'text-danger-600' : summary.netVarianceValue > 0 ? 'text-success-600' : 'text-dark-500'
          }`}>
            {summary.netVarianceValue >= 0 ? '+' : ''}{formatCentimes(summary.netVarianceValue, currency)}
          </div>
        </div>
      </div>

      {/* Variance breakdown */}
      {hasVariances && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-success-50 border border-success-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-success-700 mb-2">
              <TrendingUp className="w-5 h-5" />
              <span className="font-medium">Excedents</span>
            </div>
            <div className="text-2xl font-semibold text-success-700">
              +{formatCentimes(summary.positiveVarianceValue, currency)}
            </div>
            <div className="text-sm text-success-600 mt-1">
              {formatQuantity(summary.totalPositiveVariance)} unites en plus
            </div>
          </div>

          <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-danger-700 mb-2">
              <TrendingDown className="w-5 h-5" />
              <span className="font-medium">Manquants</span>
            </div>
            <div className="text-2xl font-semibold text-danger-700">
              {formatCentimes(summary.negativeVarianceValue, currency)}
            </div>
            <div className="text-sm text-danger-600 mt-1">
              {formatQuantity(Math.abs(summary.totalNegativeVariance))} unites en moins
            </div>
          </div>
        </div>
      )}

      {/* Warning if uncounted lines */}
      {uncountedLines > 0 && (
        <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-warning-700">
            <AlertTriangle className="w-5 h-5" />
            <span className="font-medium">Attention</span>
          </div>
          <p className="text-sm text-warning-600 mt-2">
            {uncountedLines} produit(s) n'ont pas ete compte(s). Ces lignes seront ignorees lors de la validation.
          </p>
        </div>
      )}

      {/* Lines with variance */}
      {hasVariances && (
        <div>
          <h3 className="text-sm font-medium text-neutral-700 mb-3">
            Detail des ecarts
          </h3>
          <div className="border border-neutral-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-neutral-100">
                <tr>
                  <th className="text-left px-4 py-2 text-sm font-medium text-neutral-700">
                    Produit
                  </th>
                  <th className="text-right px-4 py-2 text-sm font-medium text-neutral-700">
                    Theorique
                  </th>
                  <th className="text-right px-4 py-2 text-sm font-medium text-neutral-700">
                    Compte
                  </th>
                  <th className="text-right px-4 py-2 text-sm font-medium text-neutral-700">
                    Ecart
                  </th>
                  <th className="text-right px-4 py-2 text-sm font-medium text-neutral-700">
                    Valeur
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {linesWithVariance.map((line) => (
                  <tr key={line.lineId} className="hover:bg-neutral-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-neutral-400" />
                        <div>
                          <div className="text-sm font-medium text-dark-500">
                            {line.rawProduct.name}
                          </div>
                          <div className="text-xs text-neutral-500">
                            {line.rawProduct.sku}
                          </div>
                          {/* Detail packaging (Sprint 21b) */}
                          {line.countDetail && (
                            <div className="text-xs text-neutral-400 mt-0.5 italic">
                              {line.countDetail}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-neutral-700">
                      {line.theoreticalQty} {line.rawProduct.recipeUnit.symbol}
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-neutral-700">
                      {line.countedQty} {line.rawProduct.recipeUnit.symbol}
                    </td>
                    <td className={`px-4 py-3 text-right text-sm font-medium ${
                      line.variance < 0 ? 'text-danger-600' : 'text-success-600'
                    }`}>
                      {line.variance > 0 ? '+' : ''}{line.variance} {line.rawProduct.recipeUnit.symbol}
                    </td>
                    <td className={`px-4 py-3 text-right text-sm font-medium ${
                      line.varianceValue < 0 ? 'text-danger-600' : 'text-success-600'
                    }`}>
                      {line.varianceValue >= 0 ? '+' : ''}{formatCentimes(line.varianceValue, currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Success message if no variances */}
      {!hasVariances && summary.countedLines > 0 && (
        <div className="bg-success-50 border border-success-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-success-700">
            <Check className="w-5 h-5" />
            <span className="font-medium">Aucun ecart detecte</span>
          </div>
          <p className="text-sm text-success-600 mt-2">
            Les quantites comptees correspondent aux quantites theoriques.
          </p>
        </div>
      )}

      {/* Confirmation text */}
      <div className="bg-neutral-100 rounded-lg p-4 text-sm text-neutral-600">
        <p>
          En validant cet inventaire :
        </p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>L'inventaire sera marque comme valide et ne pourra plus etre modifie</li>
          {movementsToGenerate > 0 && (
            <li>{movementsToGenerate} mouvement(s) de stock seront genere(s) pour corriger les ecarts</li>
          )}
          <li>Le stock theorique sera ajuste en fonction des quantites comptees</li>
        </ul>
      </div>

      {/* Confirmation checkbox */}
      <div className="border-t border-neutral-200 pt-4">
        <Checkbox
          id="confirm-validation"
          checked={isConfirmed}
          onChange={(e) => onConfirmationChange(e.target.checked)}
          label="Je confirme avoir verifie les ecarts et souhaite valider cet inventaire"
        />
      </div>
    </div>
  );
}
